import { useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { FileDropZone, type FileDropZoneHandle } from "@/components/common/FileDropZone";
import { Button } from "@/components/design/Button";
import { DocumentPickerDialog, type PickedDocument } from "@/components/share/DocumentPickerDialog";
import { CreateDocumentDocument } from "@/graphql/generated/operations";
import { ingestFiles } from "@/lib/file-ingest";
import { MAX_DOCUMENT_UPLOAD_BYTES, MB, PROJECT_DOCUMENT_ACCEPT } from "@/lib/upload-limits";
import { userErrorMessage } from "@/lib/user-error";
import { useMe } from "@/auth/use-me";

// Dokumente am Projekt, gebaut wie im Match (ChallengeStep · ProjectDocumentDropZone):
// Kopfzeile mit den beiden Wegen hinein, darunter die Ablagefläche, und sobald
// etwas da ist, Kacheln statt einer Textzeile.
//
// Der Unterschied zur ersten Fassung ist nicht Kosmetik: Die Ablagefläche wird
// zur OVERLAY-Variante, sobald Dateien da sind. Eine dauerhaft sichtbare
// gestrichelte Zone über einer Dateiliste sagt "hier fehlt noch was", obwohl
// längst etwas da ist - man kann weiterhin überall hineinziehen, nur sieht man
// jetzt die Dateien statt der Aufforderung.
//
// Zwei Wege hinein, und sie tun verschiedene Dinge:
// - HOCHLADEN legt die Datei in der eigenen Bibliothek an (`createDocument`) und
//   hängt danach ihre Id ans Projekt.
// - AUS MEINEN DOKUMENTEN verweist auf ein bestehendes Dokument, ohne es ein
//   zweites Mal hochzuladen. Dafür liefert der Picker seit Neuestem die Id mit.
// Die Datei gehört in beiden Fällen backdrop, die Beziehung dem Projekt
// (@external-adr GENESIS-0013-backdrop-blob-storage-boundary).

export interface ProjectDocumentRef {
  id: string;
  name: string;
  /** Nur bekannt, wenn die Datei in dieser Sitzung durch die Hand ging. */
  size?: number;
}

export function ProjectDocuments({
  documents,
  onChange,
  readOnly = false,
}: {
  documents: readonly ProjectDocumentRef[];
  onChange: (next: ProjectDocumentRef[]) => void;
  /** Nur lesen (`Project.actions.canUpdate`): Kacheln ohne Ablegen, Hinzufügen und Entfernen. */
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const me = useMe();
  const [createDocument] = useMutation(CreateDocumentDocument);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picker, setPicker] = useState(false);
  const dropZoneRef = useRef<FileDropZoneHandle>(null);

  async function upload(files: FileList) {
    if (!me.id) return;
    setBusy(true);
    setError(null);
    const added: ProjectDocumentRef[] = [];
    try {
      // Dieselbe Grenze wie auf der Dokumentenseite: zu große Dateien werden genannt und nicht
      // hochgeladen, die übrigen schon (spark#1001).
      const { accepted, rejected } = await ingestFiles(files, { maxBytes: MAX_DOCUMENT_UPLOAD_BYTES });
      if (rejected.length) {
        setError(
          intl.formatMessage(
            { id: "project.documents.tooLarge", defaultMessage: "Zu groß (max. {max} MB pro Datei): {names}" },
            { max: MAX_DOCUMENT_UPLOAD_BYTES / MB, names: rejected.map((r) => r.file.name).join(", ") },
          ),
        );
      }
      for (const file of accepted) {
        const response = await createDocument({
          variables: {
            input: {
              data: {
                name: file.name,
                person: { id: me.id },
                tags: [],
                fileName: file.name,
                content: { mimeType: file.mimeType, content: file.content },
              },
              dryRun: false,
            },
          },
        });
        const payload = response.data?.createDocument;
        const saved = payload && "result" in payload ? payload.result : null;
        if (saved?.id) added.push({ id: saved.id, name: saved.name ?? file.name, size: file.size });
      }
      if (added.length) onChange([...documents, ...added]);
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      setBusy(false);
    }
  }

  function addPicked(picked: PickedDocument[]) {
    // Base64 wächst um ein Drittel; für die Anzeige reicht die Rückrechnung.
    const known = new Set(documents.map((d) => d.id));
    const next = picked
      .filter((p) => !known.has(p.id))
      .map((p) => ({ id: p.id, name: p.name, size: Math.round((p.content.length * 3) / 4) }));
    if (next.length) onChange([...documents, ...next]);
  }

  // Feste Kachelbreite statt Spalten, die sich die Fläche teilen: bei einer
  // einzigen Datei blies ein Raster aus vier Spalten die eine Kachel auf
  // Postkartengrösse auf. Jetzt bleibt sie klein, egal wie viele es sind.
  const tiles =
    documents.length > 0 ? (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(9rem,9rem))] gap-2">
        {documents.map((doc) => (
          <DocumentTile
            key={doc.id}
            id={`project-document-${doc.id}`}
            name={doc.name}
            size={doc.size}
            onRemove={readOnly ? undefined : () => onChange(documents.filter((d) => d.id !== doc.id))}
          />
        ))}
      </div>
    ) : undefined;

  return (
    <div className="bg-page box-flat p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
          <FormattedMessage id="project.workspace.documents" defaultMessage="Dokumente" />
        </div>
        {readOnly ? null : (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button id="project-documents-pick" variant="secondary" size="sm" onClick={() => setPicker(true)}>
              <FormattedMessage id="share.create.fromDocuments" defaultMessage="From my documents" />
            </Button>
            {documents.length > 0 ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => dropZoneRef.current?.openPicker()}
              >
                <FormattedMessage id="service.page.uploadFile" defaultMessage="Upload file" />
              </Button>
            ) : null}
          </div>
        )}
      </div>

      {readOnly ? (
        <div className="mt-2">
          {tiles ?? (
            <p className="smycles-field-meta text-ink-muted">
              <FormattedMessage id="project.documents.empty" defaultMessage="Noch keine Dokumente." />
            </p>
          )}
        </div>
      ) : (
        <FileDropZone
          ref={dropZoneRef}
          inputId="project-documents-input"
          variant={documents.length === 0 ? "zone" : "overlay"}
          clickToOpen={documents.length === 0}
          multiple
          disabled={busy}
          accept={PROJECT_DOCUMENT_ACCEPT}
          className="mt-2 rounded-md"
          onFiles={(files) => void upload(files)}
          ariaLabel={intl.formatMessage({
            id: "project.documents.ariaLabel",
            defaultMessage: "Dokumente hinzufügen",
          })}
          label={intl.formatMessage({
            id: "project.documents.dropLabel",
            defaultMessage: "Datei hier ablegen oder klicken",
          })}
          hint={intl.formatMessage({
            id: "project.documents.dropHint",
            defaultMessage: "max. 10 MB pro Datei, landet auch in deinen Dokumenten",
          })}
        >
          {tiles}
        </FileDropZone>
      )}

      {error ? (
        <div id="project-documents-error" role="alert" className="mt-2 smycles-field-meta text-ink">
          {error}
        </div>
      ) : null}

      <DocumentPickerDialog
        idPrefix="project-documents-picker"
        open={picker}
        kind="file"
        onClose={() => setPicker(false)}
        onPick={addPicked}
      />
    </div>
  );
}

/**
 * Die Kachel eines Dokuments. Exportiert, weil der Weg ins Match dieselbe
 * Darstellung braucht: was dort mitgeht, muss aussehen wie das, was hier liegt,
 * sonst lernt man dasselbe Dokument zweimal.
 *
 * `dropped` ist der abgewählte Zustand - die Kachel bleibt stehen, damit man
 * sieht, was man liegen lässt, und sie zurückholen kann. Ohne ihn wäre das
 * Entfernen eine Einbahnstrasse.
 */
export function DocumentTile({
  name,
  size,
  onRemove,
  dropped = false,
  id,
}: {
  /** Testanker: an der Kachel, `${id}-name` und `${id}-remove`. */
  id?: string;
  name: string;
  size?: number;
  /** Fehlt, wenn man nur lesen darf: dann gibt es kein Entfernen. */
  onRemove?: () => void;
  dropped?: boolean;
}) {
  const intl = useIntl();
  const kb = size ? Math.round(size / 1024) : 0;
  return (
    <div id={id} className={`bg-page box-flat group relative overflow-hidden transition ${dropped ? "opacity-40" : ""}`}>
      {onRemove ? (
        <button
          id={id && `${id}-remove`}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className={`absolute right-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-smycles-micro text-ink transition focus-visible:opacity-100 ${
            dropped ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
          title={
            dropped
              ? intl.formatMessage({ id: "project.toMatch.documentBack", defaultMessage: "Doch mitnehmen" })
              : intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })
          }
        >
          {dropped ? "+" : "✕"}
        </button>
      ) : null}
      <div className="flex aspect-[4/3] items-center justify-center text-smycles-display-s text-ink-muted">📄</div>
      <div className="px-2 py-1.5">
        <div id={id && `${id}-name`} className="truncate text-smycles-label font-medium">{name}</div>
        {size ? (
          <div className="text-smycles-micro text-ink-muted">{kb < 1024 ? `${kb} KB` : `${(kb / 1024).toFixed(1)} MB`}</div>
        ) : null}
      </div>
    </div>
  );
}
